import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import * as XLSX from 'xlsx';
import { AuthorizedApiService } from '@core';
import { CompanySelectionService } from '@core/service/company-selection.service';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { CompanySelectComponent } from '@shared/components/company-select/company-select.component';
import { OrgUnitPickerDialogComponent, OrgUnitPickerNode } from './org-unit-picker-dialog.component';
import {
  EmployeeImportError,
  EmployeeImportResponse,
  EmployeeImportResult,
  EmployeeImportRowPayload,
} from './employee.interface';

export interface EmployeeImportCompanyOption {
  companiesRecordId: string;
  name: string;
  isActive: boolean;
}

export interface EmployeeImportOrgUnitOption {
  orgUnitId: string;
  companiesRecordId: string;
  code?: string;
  name: string;
  parentId?: string | null;
  type?: string | null;
  orgPath?: string | null;
  isActive?: boolean;
}

export interface EmployeeImportDialogData {
  companies: EmployeeImportCompanyOption[];
  orgUnits: EmployeeImportOrgUnitOption[];
  employeeNumberingMode: 'manual' | 'auto' | 'hybrid';
  employeeNumberPadLength: number;
}

interface EmployeeImportDraft {
  rowNumber: number;
  employeeNumber: string;
  firstName: string;
  lastName: string;
  secondLastName: string;
  email: string;
  password: string;
  hireDate: string;
  orgUnitCode: string;
  orgUnitId: string;
  orgUnitResolvedFromCode: boolean;
  errors: string[];
}

interface EmployeeImportGeneratedCredential {
  resultRowNumber: number;
  rowNumber: number;
  employeeNumber: string;
  fullName: string;
  email: string;
  temporaryPassword: string;
  copied: boolean;
}

type ImportStep = 'file' | 'organization' | 'result';

@Component({
  selector: 'app-employee-import-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    CompanySelectComponent,
    TranslateModule,
  ],
  templateUrl: './employee-import-dialog.component.html',
  styleUrl: './employee-import-dialog.component.scss',
})
export class EmployeeImportDialogComponent {
  private static readonly MAX_ROWS = 500;
  private static readonly REQUIRED_HEADERS = [
    'numero_empleado',
    'apellido_paterno',
    'apellido_materno',
    'nombre',
    'fecha_contratacion',
  ];
  private static readonly TEMPLATE_HEADERS = [
    'numero_empleado',
    'apellido_paterno',
    'apellido_materno',
    'nombre',
    'correo',
    'fecha_contratacion',
    'contrasena',
    'clave_unidad_organizacional',
  ];

  private readonly dialogRef = inject(MatDialogRef<EmployeeImportDialogComponent>);
  private readonly dialog = inject(MatDialog);
  private readonly apiService = inject(AuthorizedApiService);
  private readonly translate = inject(TranslateService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly companySelection = inject(CompanySelectionService);
  readonly data = inject<EmployeeImportDialogData>(MAT_DIALOG_DATA);

  step: ImportStep = 'file';
  fileName = '';
  rows: EmployeeImportDraft[] = [];
  fileError = '';
  selectedCompanyId = this.companySelection.resolveSelection(this.data.companies);
  isReading = false;
  isImporting = false;
  importResponse: EmployeeImportResponse | null = null;
  importError = '';
  generatedCredentials: EmployeeImportGeneratedCredential[] = [];
  private orgUnitCacheCompanyId = '';
  private orgUnitPathById = new Map<string, string>();
  private orgUnitPickerTree: OrgUnitPickerNode[] = [];

  get invalidRows(): EmployeeImportDraft[] {
    return this.rows.filter((row) => row.errors.length > 0);
  }

  get canContinue(): boolean {
    return this.rows.length > 0 && this.invalidRows.length === 0 && !this.isReading;
  }

  get availableOrgUnits(): EmployeeImportOrgUnitOption[] {
    const companyUnits = this.availableOrgUnitTreeUnits;
    const parentIds = new Set(
      companyUnits
        .map((unit) => unit.parentId)
        .filter((parentId): parentId is string => typeof parentId === 'string' && !!parentId.trim())
    );
    return companyUnits
      .filter((unit) => !parentIds.has(unit.orgUnitId))
      .sort((left, right) => left.name.localeCompare(right.name));
  }

  get availableOrgUnitTreeUnits(): EmployeeImportOrgUnitOption[] {
    return this.data.orgUnits
      .filter((unit) => unit.companiesRecordId === this.selectedCompanyId && unit.isActive !== false)
      .sort((left, right) => left.name.localeCompare(right.name));
  }

  get canImport(): boolean {
    return this.isOrganizationReady && !this.isImporting;
  }

  get hasCopyableGeneratedCredentials(): boolean {
    return this.generatedCredentials.some((credential) => !!credential.temporaryPassword);
  }

  get isOrganizationReady(): boolean {
    return this.canContinue && !!this.selectedCompanyId && this.pendingOrgUnitRows.length === 0;
  }

  get pendingOrgUnitRows(): EmployeeImportDraft[] {
    return this.rows.filter((row) => !row.orgUnitId);
  }

  get selectedCompanyName(): string {
    return this.data.companies.find((company) => company.companiesRecordId === this.selectedCompanyId)?.name || '—';
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) {
      return;
    }

    this.resetFileState();
    this.fileName = file.name;
    this.isReading = true;
    try {
      const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: false });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      if (!sheet) {
        throw new Error(this.t('employeesCatalog.import.errors.emptyWorkbook'));
      }
      this.rows = this.parseSheet(sheet);
      if (this.selectedCompanyId) {
        this.resolveOrgUnitAssignments();
      }
      if (!this.rows.length) {
        this.fileError = this.t('employeesCatalog.import.errors.noRows');
      }
    } catch (error) {
      console.error('Error reading employee import file', error);
      this.rows = [];
      this.fileError = error instanceof Error && error.message
        ? error.message
        : this.t('employeesCatalog.import.errors.readError');
    } finally {
      this.isReading = false;
    }
  }

  downloadTemplate(): void {
    const rows = [EmployeeImportDialogComponent.TEMPLATE_HEADERS];
    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    worksheet['!cols'] = [
      { wch: 20 }, { wch: 22 }, { wch: 22 }, { wch: 22 }, { wch: 32 }, { wch: 18 }, { wch: 24 }, { wch: 32 },
    ];
    for (let row = 2; row <= EmployeeImportDialogComponent.MAX_ROWS + 1; row++) {
      for (const column of ['A', 'F', 'G', 'H']) {
        worksheet[`${column}${row}`] = { t: 's', v: '', z: '@' };
      }
    }
    worksheet['!ref'] = `A1:H${EmployeeImportDialogComponent.MAX_ROWS + 1}`;

    const instructions = XLSX.utils.aoa_to_sheet([
      [this.t('employeesCatalog.import.template.instructionsTitle')],
      [this.t('employeesCatalog.import.template.instructionsEmployeeNumber')],
      [this.t('employeesCatalog.import.template.instructionsRequired')],
      [this.t('employeesCatalog.import.template.instructionsPassword')],
      [this.t('employeesCatalog.import.template.instructionsOrgUnit')],
      [this.t('employeesCatalog.import.template.instructionsLimit', { count: EmployeeImportDialogComponent.MAX_ROWS })],
    ]);
    instructions['!cols'] = [{ wch: 100 }];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Empleados');
    XLSX.utils.book_append_sheet(workbook, instructions, 'Instrucciones');
    XLSX.writeFile(workbook, 'plantilla-importacion-empleados.xlsx');
  }

  continueToOrganization(): void {
    if (this.canContinue) {
      this.step = 'organization';
    }
  }

  backToFile(): void {
    if (!this.isImporting) {
      this.step = 'file';
    }
  }

  onCompanyChange(): void {
    this.companySelection.rememberSelection(this.selectedCompanyId);
    this.clearOrgUnitCache();
    this.resolveOrgUnitAssignments();
  }

  openOrgUnitPicker(row: EmployeeImportDraft): void {
    if (!this.selectedCompanyId) {
      return;
    }

    this.ensureOrgUnitCache();
    const nodes = this.orgUnitPickerTree;
    if (!nodes.length) {
      return;
    }

    const pickerRef = this.dialog.open(OrgUnitPickerDialogComponent, {
      width: '920px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      autoFocus: false,
      data: {
        nodes,
        selectedOrgUnitId: row.orgUnitId,
      },
    });

    pickerRef.afterClosed().subscribe((selectedNode: OrgUnitPickerNode | null | undefined) => {
      if (selectedNode === undefined) {
        return;
      }

      row.orgUnitId = selectedNode?.orgUnitId || '';
      row.orgUnitResolvedFromCode = false;
    });
  }

  async importEmployees(): Promise<void> {
    if (!this.canImport) {
      return;
    }

    this.isImporting = true;
    this.importError = '';
    try {
      const payload = {
        rows: this.rows.map((row): EmployeeImportRowPayload => ({
          ...(row.employeeNumber ? { employeeNumber: row.employeeNumber } : {}),
          firstName: row.firstName,
          lastName: row.lastName,
          ...(row.secondLastName ? { secondLastName: row.secondLastName } : {}),
          ...(row.email ? { email: row.email } : {}),
          ...(row.password ? { password: row.password } : {}),
          hireDate: row.hireDate,
          companiesRecordId: this.selectedCompanyId,
          orgUnitId: row.orgUnitId,
        })),
      };
      const response = await firstValueFrom(
        this.apiService.post<EmployeeImportResponse>('employees/import', payload)
      );
      this.importResponse = this.normalizeImportResponse(response);
      this.generatedCredentials = this.extractGeneratedCredentials(this.importResponse);
      this.step = 'result';
      this.notifyImportResult(this.importResponse);
    } catch (error) {
      console.error('Error importing employees', error);
      this.importError = this.resolveImportRequestError(error);
      this.showNotification('snackbar-danger', this.importError);
    } finally {
      this.isImporting = false;
    }
  }

  close(): void {
    const hasCreatedEmployees = (this.importResponse?.summary?.created || 0) > 0;
    this.generatedCredentials.forEach((credential) => {
      credential.temporaryPassword = '';
    });
    this.generatedCredentials = [];
    this.dialogRef.close(hasCreatedEmployees);
  }

  private parseSheet(sheet: XLSX.WorkSheet): EmployeeImportDraft[] {
    const matrix = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
      header: 1,
      defval: '',
      raw: false,
      blankrows: false,
    });
    if (!matrix.length) {
      return [];
    }

    const normalizedHeaders = matrix[0].map((value) => this.normalizeHeader(value));
    const indexes = new Map(normalizedHeaders.map((header, index) => [header, index]));
    const missingHeaders = EmployeeImportDialogComponent.REQUIRED_HEADERS.filter((header) => !indexes.has(header));
    if (missingHeaders.length) {
      throw new Error(this.t('employeesCatalog.import.errors.missingHeaders', { headers: missingHeaders.join(', ') }));
    }

    const dataRows = matrix.slice(1)
      .map((values, index) => ({ values, rowNumber: index + 2 }))
      .filter(({ values }) => values.some((value) => this.toText(value) !== ''));
    if (dataRows.length > EmployeeImportDialogComponent.MAX_ROWS) {
      throw new Error(this.t('employeesCatalog.import.errors.rowLimit', { count: EmployeeImportDialogComponent.MAX_ROWS }));
    }

    const drafts = dataRows.map(({ values, rowNumber }) => {
      const value = (header: string): string => {
        const columnIndex = indexes.get(header);
        return columnIndex === undefined ? '' : this.toText(values[columnIndex]);
      };
      const upperValue = (header: string): string => value(header).toLocaleUpperCase('es-MX');
      const rawHireDate = value('fecha_contratacion');
      return {
        rowNumber,
        employeeNumber: value('numero_empleado'),
        firstName: upperValue('nombre'),
        lastName: upperValue('apellido_paterno'),
        secondLastName: upperValue('apellido_materno'),
        email: value('correo'),
        password: value('contrasena'),
        hireDate: this.normalizeHireDate(rawHireDate) || rawHireDate,
        orgUnitCode: value('clave_unidad_organizacional'),
        orgUnitId: '',
        orgUnitResolvedFromCode: false,
        errors: [],
      };
    });

    const employeeNumberCounts = new Map<string, number>();
    drafts.forEach((row) => {
      const key = row.employeeNumber.toLocaleLowerCase();
      if (key) {
        employeeNumberCounts.set(key, (employeeNumberCounts.get(key) || 0) + 1);
      }
    });
    drafts.forEach((row) => this.validateRow(row, employeeNumberCounts));
    return drafts;
  }

  private validateRow(row: EmployeeImportDraft, employeeNumberCounts: Map<string, number>): void {
    if (this.data.employeeNumberingMode === 'manual' && !row.employeeNumber) {
      row.errors.push(this.t('employeesCatalog.import.errors.employeeNumberRequired'));
    }
    if (
      this.data.employeeNumberingMode === 'manual' &&
      row.employeeNumber &&
      this.data.employeeNumberPadLength > 0 &&
      row.employeeNumber.length !== this.data.employeeNumberPadLength
    ) {
      row.errors.push(this.t('employeesCatalog.import.errors.employeeNumberLength', { length: this.data.employeeNumberPadLength }));
    }
    if (row.employeeNumber && (employeeNumberCounts.get(row.employeeNumber.toLocaleLowerCase()) || 0) > 1) {
      row.errors.push(this.t('employeesCatalog.import.errors.duplicateEmployeeNumber'));
    }
    if (!row.firstName) {
      row.errors.push(this.t('employeesCatalog.import.errors.firstNameRequired'));
    }
    if (!row.lastName) {
      row.errors.push(this.t('employeesCatalog.import.errors.lastNameRequired'));
    }
    if (!row.hireDate) {
      row.errors.push(this.t('employeesCatalog.import.errors.hireDateRequired'));
    } else if (!this.normalizeHireDate(row.hireDate)) {
      row.errors.push(this.t('employeesCatalog.import.errors.hireDateFormat'));
    }
    if (row.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) {
      row.errors.push(this.t('employeesCatalog.import.errors.invalidEmail'));
    }
    if (row.password && row.password.length < 8) {
      row.errors.push(this.t('employeesCatalog.import.errors.passwordMinLength'));
    }
  }

  private normalizeHeader(value: unknown): string {
    const normalized = this.toText(value)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
    const aliases: Record<string, string> = {
      numero_de_empleado: 'numero_empleado',
      employee_number: 'numero_empleado',
      firstname: 'nombre',
      first_name: 'nombre',
      lastname: 'apellido_paterno',
      last_name: 'apellido_paterno',
      secondlastname: 'apellido_materno',
      second_last_name: 'apellido_materno',
      email: 'correo',
      password: 'contrasena',
      hire_date: 'fecha_contratacion',
      hiredate: 'fecha_contratacion',
      fecha_alta: 'fecha_contratacion',
      enrollment_date: 'fecha_contratacion',
      enrolment_date: 'fecha_contratacion',
      enrole_date: 'fecha_contratacion',
      org_unit_code: 'clave_unidad_organizacional',
      orgunitcode: 'clave_unidad_organizacional',
      clave_org_unit: 'clave_unidad_organizacional',
      clave_unidad: 'clave_unidad_organizacional',
    };
    return aliases[normalized] || normalized;
  }

  private isValidIsoDate(value: string): boolean {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) {
      return false;
    }

    const [, yearText, monthText, dayText] = match;
    const year = Number(yearText);
    const month = Number(monthText);
    const day = Number(dayText);
    const parsed = new Date(Date.UTC(year, month - 1, day));
    return parsed.getUTCFullYear() === year
      && parsed.getUTCMonth() === month - 1
      && parsed.getUTCDate() === day;
  }

  private normalizeHireDate(value: string): string | null {
    const trimmed = value.trim();
    if (this.isValidIsoDate(trimmed)) {
      const [, year, month, day] = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/) || [];
      return `${day}-${month}-${year}`;
    }

    const match = trimmed.match(/^(\d{2})([-/])(\d{2})\2(\d{4})$/);
    if (!match) {
      return null;
    }

    const [, dayText, separator, monthText, yearText] = match;
    const isoDate = `${yearText}-${monthText}-${dayText}`;
    return this.isValidIsoDate(isoDate)
      ? `${dayText}${separator}${monthText}${separator}${yearText}`
      : null;
  }

  formatHireDateForDisplay(value: string): string {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return match ? `${match[3]}-${match[2]}-${match[1]}` : value;
  }

  private notifyImportResult(response: EmployeeImportResponse): void {
    const { total, created, failed } = response.summary;
    const failureDetail = this.buildImportFailureDetail(response);
    if (created > 0 && failed === 0) {
      this.showNotification(
        'snackbar-success',
        this.t('employeesCatalog.import.notifications.success', { count: created })
      );
      return;
    }
    if (created > 0 && failed > 0) {
      this.showNotification(
        'snackbar-warning',
        `${this.t('employeesCatalog.import.notifications.partial', { created, total })}${failureDetail}`
      );
      return;
    }
    this.showNotification(
      'snackbar-danger',
      `${this.t('employeesCatalog.import.notifications.failed', { count: failed || total })}${failureDetail}`
    );
  }

  private buildImportFailureDetail(response: EmployeeImportResponse): string {
    const reasons = [...new Set(
      response.results
        .flatMap((result) => result.errors || [])
        .map((error) => this.getImportErrorMessage(error).trim())
        .filter((message): message is string => !!message)
    )];
    if (!reasons.length) {
      return '';
    }
    const visibleReasons = reasons.slice(0, 2).join(' · ');
    const remaining = reasons.length - 2;
    const additional = remaining > 0
      ? ` ${this.t('employeesCatalog.import.notifications.additionalReasons', { count: remaining })}`
      : '';
    return ` ${this.t('employeesCatalog.import.notifications.reason', { reason: visibleReasons })}${additional}`;
  }

  private showNotification(panelClass: string, message: string): void {
    this.snackBar.open(message, '', {
      duration: panelClass === 'snackbar-success' ? 4500 : 7000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: [panelClass],
    });
  }

  getImportDisplayRowNumber(result: EmployeeImportResult): number {
    return this.rows[result.rowNumber - 1]?.rowNumber || result.rowNumber;
  }

  getImportEmployeeFullName(result: EmployeeImportResult): string {
    const row = this.rows[result.rowNumber - 1];
    return row ? [row.firstName, row.lastName, row.secondLastName].filter(Boolean).join(' ') : '—';
  }

  getImportEmployeeEmail(result: EmployeeImportResult): string {
    return this.rows[result.rowNumber - 1]?.email || result.userProvisioning?.email || '—';
  }

  getGeneratedCredential(result: EmployeeImportResult): EmployeeImportGeneratedCredential | undefined {
    return this.generatedCredentials.find((credential) => credential.resultRowNumber === result.rowNumber);
  }

  getImportErrorMessage(error: EmployeeImportError): string {
    const key = `employeesCatalog.import.errorCodes.${error.code}`;
    const translated = this.t(key, { field: this.getImportErrorFieldLabel(error.field) });
    return translated !== key
      ? translated
      : error.message || this.t('employeesCatalog.import.errorCodes.EMPLOYEE_IMPORT_ROW_FAILED');
  }

  getImportErrorFieldLabel(field: string | null): string {
    if (!field) {
      return '';
    }

    const keys: Record<string, string> = {
      firstName: 'employeesCatalog.fields.firstName',
      lastName: 'employeesCatalog.fields.lastName',
      secondLastName: 'employeesCatalog.fields.secondLastName',
      employeeNumber: 'employeesCatalog.fields.employeeNumber',
      email: 'employeesCatalog.fields.email',
      phone: 'employeesCatalog.fields.phone',
      hireDate: 'employeesCatalog.fields.hireDate',
      terminationDate: 'employeesCatalog.fields.terminationDate',
      companiesRecordId: 'employeesCatalog.fields.company',
      orgUnitId: 'employeesCatalog.fields.orgUnit',
      password: 'employeesCatalog.import.credentials.password',
    };
    const key = keys[field];
    return key ? this.t(key) : field;
  }

  async copyGeneratedCredential(credential: EmployeeImportGeneratedCredential): Promise<void> {
    if (!credential.temporaryPassword) {
      return;
    }

    try {
      await navigator.clipboard.writeText(credential.temporaryPassword);
      credential.copied = true;
      this.showNotification('snackbar-success', this.t('employeesCatalog.import.credentials.copied'));
    } catch {
      this.showNotification('snackbar-danger', this.t('employeesCatalog.import.credentials.copyError'));
    }
  }

  async copyAllGeneratedCredentials(): Promise<void> {
    const availableCredentials = this.generatedCredentials.filter((credential) => credential.temporaryPassword);
    if (!availableCredentials.length) {
      return;
    }

    const content = availableCredentials
      .map((credential) => [credential.employeeNumber, credential.fullName, credential.email, credential.temporaryPassword].join('\t'))
      .join('\n');
    try {
      await navigator.clipboard.writeText(content);
      availableCredentials.forEach((credential) => {
        credential.copied = true;
      });
      this.showNotification('snackbar-success', this.t('employeesCatalog.import.credentials.allCopied'));
    } catch {
      this.showNotification('snackbar-danger', this.t('employeesCatalog.import.credentials.copyError'));
    }
  }

  downloadGeneratedCredentials(): void {
    const credentials = this.generatedCredentials.filter((credential) => credential.temporaryPassword);
    if (!credentials.length) {
      return;
    }

    try {
      const worksheet = XLSX.utils.aoa_to_sheet([
        [
          this.t('employeesCatalog.fields.employeeNumber'),
          this.t('employeesCatalog.fields.fullName'),
          this.t('employeesCatalog.fields.email'),
          this.t('employeesCatalog.import.credentials.password'),
        ],
        ...credentials.map((credential) => [
          credential.employeeNumber,
          credential.fullName,
          credential.email,
          credential.temporaryPassword,
        ]),
      ]);
      worksheet['!cols'] = [{ wch: 20 }, { wch: 38 }, { wch: 34 }, { wch: 28 }];
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Contraseñas');
      XLSX.writeFile(workbook, `contrasenas-temporales-empleados-${new Date().toISOString().slice(0, 10)}.xlsx`);
      this.showNotification('snackbar-success', this.t('employeesCatalog.import.credentials.downloaded'));
    } catch {
      this.showNotification('snackbar-danger', this.t('employeesCatalog.import.credentials.downloadError'));
    }
  }

  private normalizeImportResponse(response: EmployeeImportResponse): EmployeeImportResponse {
    const results = Array.isArray(response?.results)
      ? response.results.map((result) => ({
          ...result,
          errors: Array.isArray(result.errors)
            ? result.errors.map((error) => ({
                field: typeof error?.field === 'string' ? error.field : null,
                code: typeof error?.code === 'string' && error.code ? error.code : 'EMPLOYEE_IMPORT_ROW_FAILED',
                message: typeof error?.message === 'string' ? error.message : '',
              }))
            : [],
        }))
      : [];
    const created = results.filter((result) => result.status === 'created').length;
    const failed = results.length - created;
    return {
      importId: typeof response?.importId === 'string' ? response.importId : '',
      summary: {
        total: Number.isFinite(response?.summary?.total) ? response.summary.total : results.length,
        created: Number.isFinite(response?.summary?.created) ? response.summary.created : created,
        failed: Number.isFinite(response?.summary?.failed) ? response.summary.failed : failed,
      },
      results,
    };
  }

  private extractGeneratedCredentials(response: EmployeeImportResponse): EmployeeImportGeneratedCredential[] {
    return response.results.flatMap((result) => {
      const provisioning = result.userProvisioning;
      if (
        result.status !== 'created' ||
        provisioning?.created !== true ||
        provisioning.passwordSource !== 'generated' ||
        !provisioning.temporaryPassword
      ) {
        return [];
      }

      const temporaryPassword = provisioning.temporaryPassword;
      provisioning.temporaryPassword = undefined;

      return [{
        resultRowNumber: result.rowNumber,
        rowNumber: this.getImportDisplayRowNumber(result),
        employeeNumber: result.employeeNumber || '—',
        fullName: this.getImportEmployeeFullName(result),
        email: provisioning.email || this.rows[result.rowNumber - 1]?.email || '—',
        temporaryPassword,
        copied: false,
      }];
    });
  }

  private resolveImportRequestError(error: unknown): string {
    const record = error && typeof error === 'object' ? error as Record<string, unknown> : null;
    const status = Number(record?.['status'] || 0);
    if (status === 401) {
      return this.t('employeesCatalog.import.errors.unauthorized');
    }
    if (status === 403) {
      return this.t('employeesCatalog.import.errors.forbidden');
    }

    const nested = record?.['error'];
    const payload = nested && typeof nested === 'object' && !Array.isArray(nested)
      ? nested as Record<string, unknown>
      : record;
    const message = payload?.['message'];
    if (Array.isArray(message)) {
      const messages = message.filter((item): item is string => typeof item === 'string' && !!item.trim());
      if (messages.length) {
        return messages.join(' · ');
      }
    }

    return extractApiErrorMessage(error, this.t('employeesCatalog.import.errors.importError'));
  }

  getOrgUnitName(orgUnitId: string): string {
    return this.data.orgUnits.find((unit) => unit.orgUnitId === orgUnitId)?.name || '—';
  }

  getOrgUnitCode(orgUnitId: string): string {
    return this.data.orgUnits.find((unit) => unit.orgUnitId === orgUnitId)?.code || '';
  }

  getOrgUnitPath(orgUnitId: string): string {
    this.ensureOrgUnitCache();
    return this.orgUnitPathById.get(orgUnitId) || this.getOrgUnitName(orgUnitId);
  }

  private ensureOrgUnitCache(): void {
    if (this.orgUnitCacheCompanyId === this.selectedCompanyId) {
      return;
    }

    const units = this.availableOrgUnitTreeUnits;
    const unitsById = new Map(units.map((unit) => [unit.orgUnitId, unit]));
    const paths = new Map<string, string>();

    units.forEach((unit) => {
      const labels: string[] = [];
      const visited = new Set<string>();
      let current: EmployeeImportOrgUnitOption | undefined = unit;
      while (current && !visited.has(current.orgUnitId)) {
        labels.unshift(current.name);
        visited.add(current.orgUnitId);
        current = current.parentId ? unitsById.get(current.parentId) : undefined;
      }
      paths.set(unit.orgUnitId, labels.join(' / '));
    });

    this.orgUnitCacheCompanyId = this.selectedCompanyId;
    this.orgUnitPathById = paths;
    this.orgUnitPickerTree = this.buildOrgUnitPickerTree(units);
  }

  private clearOrgUnitCache(): void {
    this.orgUnitCacheCompanyId = '';
    this.orgUnitPathById.clear();
    this.orgUnitPickerTree = [];
  }

  private buildOrgUnitPickerTree(units: EmployeeImportOrgUnitOption[]): OrgUnitPickerNode[] {
    const nodeMap = new Map<string, OrgUnitPickerNode>();
    const roots: OrgUnitPickerNode[] = [];

    units.forEach((unit) => {
      nodeMap.set(unit.orgUnitId, {
        orgUnitId: unit.orgUnitId,
        name: unit.name,
        type: unit.type || '',
        level: 0,
        children: [],
        isLeaf: true,
        pathLabels: [],
      });
    });

    units.forEach((unit) => {
      const node = nodeMap.get(unit.orgUnitId);
      const parent = unit.parentId ? nodeMap.get(unit.parentId) : undefined;
      if (!node) {
        return;
      }
      if (parent) {
        parent.children.push(node);
        parent.isLeaf = false;
      } else {
        roots.push(node);
      }
    });

    const assignMetadata = (nodes: OrgUnitPickerNode[], path: string[] = [], level = 0): void => {
      nodes.sort((left, right) => left.name.localeCompare(right.name));
      nodes.forEach((node) => {
        node.level = level;
        node.pathLabels = [...path, node.name];
        assignMetadata(node.children, node.pathLabels, level + 1);
      });
    };

    assignMetadata(roots);
    return roots;
  }

  private resolveOrgUnitAssignments(): void {
    const unitsByCode = new Map<string, EmployeeImportOrgUnitOption[]>();
    this.availableOrgUnits.forEach((unit) => {
      const code = this.normalizeOrgUnitCode(unit.code || '');
      if (!code) {
        return;
      }
      unitsByCode.set(code, [...(unitsByCode.get(code) || []), unit]);
    });

    this.rows.forEach((row) => {
      const matches = unitsByCode.get(this.normalizeOrgUnitCode(row.orgUnitCode)) || [];
      row.orgUnitId = matches.length === 1 ? matches[0].orgUnitId : '';
      row.orgUnitResolvedFromCode = matches.length === 1;
    });
  }

  private normalizeOrgUnitCode(value: string): string {
    return value.trim().toLocaleLowerCase();
  }

  private toText(value: unknown): string {
    return value === null || value === undefined
      ? ''
      : String(value).replace(/\u00a0/g, ' ').trim();
  }

  private resetFileState(): void {
    this.rows = [];
    this.fileError = '';
    this.importError = '';
    this.importResponse = null;
    this.generatedCredentials = [];
    this.selectedCompanyId = this.companySelection.resolveSelection(this.data.companies);
    this.step = 'file';
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }
}
